/**
 * Pure logic for inventory history: consumption delta and quantity change.
 * Used by inventoryHistory.ts; fully unit-testable without DB.
 */
import { invariant } from './invariant';

export type InventoryHistoryType = 'purchase' | 'lost' | 'broken' | 'consumed' | 'adjustment';

/**
 * Compute consumption delta when editing or adding consumption.
 * quantityChangeStored: value from DB (negative for consumed).
 * newQuantityUsed: new user-entered quantity (positive).
 * Returns delta (positive = more consumed, negative = less consumed) and previouslyConsumed (positive).
 */
export function computeConsumptionDelta(
    quantityChangeStored: number | null,
    newQuantityUsed: number
): { delta: number; previouslyConsumed: number } {
    const previouslyConsumed = quantityChangeStored != null ? Math.abs(quantityChangeStored) : 0;
    const delta = newQuantityUsed - previouslyConsumed;
    return { delta, previouslyConsumed };
}

/**
 * New itemLocation quantity after applying a consumption delta.
 * delta positive = more consumed (subtract from current); delta negative = less consumed (add back).
 * Clamped to >= 0.
 */
export function computeItemLocationQuantityAfterConsumptionDelta(currentQuantity: number, delta: number): number {
    return Math.max(0, currentQuantity - delta);
}

/**
 * Quantity change to store in inventoryHistory for a given type.
 * Purchase: positive (adds to inventory). Lost/broken/consumed: negative.
 */
export function computeQuantityChangeForHistoryType(type: InventoryHistoryType, quantity: number): number {
    if (type === 'adjustment') return quantity; // adjustment quantity is the signed delta
    invariant(quantity > 0, `Quantity must be greater than 0 for history type '${type}'; got ${quantity}`);
    return type === 'purchase' ? quantity : -quantity;
}

/**
 * New itemLocation quantity after applying a history change (purchase, lost, broken, consumed).
 * quantityChange is positive for purchase, negative for lost/broken/consumed.
 * Clamped to >= 0.
 */
export function computeItemLocationQuantityAfterChange(currentQuantity: number, quantityChange: number): number {
    return Math.max(0, currentQuantity + quantityChange);
}
